import { useState, useCallback, useEffect } from 'react';
import BirthForm from '@/pages/profile/components/BirthForm';
import DestinyResult from '@/pages/profile/components/DestinyResult';
import DivinationHistory from '@/pages/profile/components/DivinationHistory';
import DestinyHistory from '@/pages/profile/components/DestinyHistory';
import type { BaziDetail } from '@/utils/lunarCalendar';
import { getUsageInfo, addPaidPackage, hasPaywallBeenShown, clearPaywallShown } from '@/utils/usageStorage';

type TabType = 'destiny' | 'history' | 'destinyRecords';

export default function Profile() {
  const [activeTab, setActiveTab] = useState<TabType>('destiny');
  const [baziDetail, setBaziDetail] = useState<BaziDetail | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [usageInfo, setUsageInfo] = useState(getUsageInfo);
  const [paywallShown, setPaywallShown] = useState(hasPaywallBeenShown);

  const handleBaziCalculated = useCallback((detail: BaziDetail) => {
    setBaziDetail(detail);
    setShowResult(true);
  }, []);

  const handleReset = useCallback(() => {
    setBaziDetail(null);
    setShowResult(false);
  }, []);

  const handleBought30 = useCallback(() => {
    addPaidPackage();
    clearPaywallShown();
    setUsageInfo(getUsageInfo());
    setPaywallShown(false);
  }, []);

  // 刷新次数信息（页面可见时更新）
  const refreshUsage = useCallback(() => {
    setUsageInfo(getUsageInfo());
    setPaywallShown(hasPaywallBeenShown());
  }, []);

  // 页面每次获得焦点时刷新
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshUsage();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refreshUsage]);

  // canUseButton: 次数用完 + 支付弹窗已被触发过
  const canUseBoughtButton = usageInfo.remaining <= 0 && paywallShown;

  return (
    <div className="flex flex-col min-h-screen pt-6 pb-6">
      <header className="w-full text-center mb-4 animate-fade-in">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-100 mb-3">
          <i className="ri-user-star-line text-3xl text-secondary-500"></i>
        </div>
        <h1 className="font-heading text-xl font-bold text-foreground-950 mb-1">我的</h1>

        {/* 使用次数 */}
        <div className="mt-2 flex items-center justify-center gap-2">
          {usageInfo.remaining > 0 ? (
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ${usageInfo.remaining <= 3 ? 'bg-accent-50 border border-accent-200' : 'bg-background-100 border border-background-200'}`}>
              <i className="ri-coins-line text-xs text-foreground-500"></i>
              <span className={`text-xs font-medium ${usageInfo.remaining <= 3 ? 'text-accent-700' : 'text-foreground-600'}`}>
                剩余 {usageInfo.remaining} 次
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-50 border border-accent-200">
              <i className="ri-error-warning-line text-xs text-accent-600"></i>
              <span className="text-xs text-accent-700 font-medium">次数已用完</span>
            </div>
          )}
          {usageInfo.hasPaid && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200">
              <i className="ri-vip-crown-line text-xs text-primary-600"></i>
              <span className="text-xs text-primary-700 font-medium">付费用户</span>
            </div>
          )}

          {/* 我已买30次按钮 - 仅当次数用完且支付弹窗已触发后才可点击 */}
          {canUseBoughtButton && (
            <button
              onClick={handleBought30}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary-500 text-background-50 text-xs font-bold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap active:scale-[0.97]"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-check-double-line"></i>
              </div>
              <span>我已买30次</span>
            </button>
          )}
        </div>
      </header>

      {/* Tab 切换 */}
      <div className="w-full mb-5">
        <div className="flex bg-background-100 rounded-full p-1 border border-background-200">
          <button
            onClick={() => setActiveTab('destiny')}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeTab === 'destiny'
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-600 hover:text-foreground-900'
            }`}
          >
            <div className="flex items-center justify-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-compass-discover-line text-base"></i>
              </div>
              <span>命格解读</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeTab === 'history'
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-600 hover:text-foreground-900'
            }`}
          >
            <div className="flex items-center justify-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-history-line text-base"></i>
              </div>
              <span>问卜记录</span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('destinyRecords')}
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeTab === 'destinyRecords'
                ? 'bg-primary-500 text-background-50'
                : 'text-foreground-600 hover:text-foreground-900'
            }`}
          >
            <div className="flex items-center justify-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-2-line text-base"></i>
              </div>
              <span>命格记录</span>
            </div>
          </button>
        </div>
      </div>

      {/* 命格解读 Tab */}
      {activeTab === 'destiny' && (
        <div className="animate-fade-in">
          {!showResult ? (
            <BirthForm onCalculated={handleBaziCalculated} />
          ) : (
            baziDetail && (
              <DestinyResult detail={baziDetail} onReset={handleReset} />
            )
          )}
        </div>
      )}

      {/* 问卜记录 Tab */}
      {activeTab === 'history' && (
        <div className="animate-fade-in">
          <DivinationHistory />
        </div>
      )}

      {/* 命格记录 Tab */}
      {activeTab === 'destinyRecords' && (
        <div className="animate-fade-in">
          <DestinyHistory />
        </div>
      )}
    </div>
  );
}